import { startTransition, useEffect, useState } from 'react';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../components/Table/Table';
import SvgIcon from '../../components/SvgIcon';
import { colors } from '../../lib/colors';
import RangeCalendar from '../../components/Calendar/RangeCalendar';
import dayjs from 'dayjs';
import { getAllCoachMemberListAPI } from '../../api/feed';
import ModalMembershipCreate from '../../components/MemberProfile/ModalMembershipCreate';
import { useNavigate } from 'react-router-dom';
import { overlay } from 'overlay-kit';
import ModalAlertOneButton from '../../components/modal/ModalAlertOneButton';

// ──────────────────────────────────────
// Types
// ──────────────────────────────────────
type SubTab = 'paymentHistory' | 'settlementHistory' | 'paymentRequest';
type SettlementSubTab = 'daily' | 'perCase';
type PaymentStatus = 'COMPLETED' | 'CANCELLED' | 'FAILED';
type SettlementStatus = 'SETTLED' | 'PENDING';
type RequestStatus = 'WAITING' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
type QuickDate = 'today' | 'week' | 'month' | 'lastMonth' | 'custom';

interface PaymentHistoryItem {
    paymentDate: string;
    memberName: string;
    coachName: string;
    membershipName: string;
    paymentStatus: PaymentStatus;
    billingMode: 'PREPAID' | 'PER_SESSION';
    paymentAmount: number;
    cancelAmount: number;
    paymentMethod: string;
    receiptUrl: string | null;
}

interface SettlementDailyItem {
    settlementDate: string;
    salesDate: string;
    totalCount: number;
    salesAmount: number;
    pgFee: number;
    pgVat: number;
    totalFee: number;
    settlementAmount: number;
    settlementStatus: SettlementStatus;
}

interface SettlementPerCaseItem {
    settlementDate: string;
    salesDate: string;
    memberName: string;
    coachName: string;
    membershipName: string;
    paymentStatus: PaymentStatus;
    paymentAmount: number;
    pgFee: number;
    vat: number;
    settlementAmount: number;
    paymentMethod: string;
    settlementStatus: SettlementStatus;
}

interface PaymentRequestItem {
    requestDate: string;
    expireDate: string;
    memberName: string;
    coachName: string;
    membershipName: string;
    itemName: string;
    requestAmount: number;
    requestStatus: RequestStatus;
    paymentDate: string | null;
}

// ──────────────────────────────────────
// Dummy Data
// ──────────────────────────────────────
const dummyPaymentHistory: PaymentHistoryItem[] = [
    { paymentDate: '2026-03-20 14:30', memberName: '김민수', coachName: '이영진', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', billingMode: 'PREPAID', paymentAmount: 800000, cancelAmount: 0, paymentMethod: '신용카드', receiptUrl: null },
    { paymentDate: '2026-03-19 11:20', memberName: '박지은', coachName: '최동석', membershipName: 'PT 10회', paymentStatus: 'COMPLETED', billingMode: 'PER_SESSION', paymentAmount: 500000, cancelAmount: 0, paymentMethod: '체크카드', receiptUrl: null },
    { paymentDate: '2026-03-18 16:45', memberName: '이수진', coachName: '이영진', membershipName: 'PT 5회', paymentStatus: 'CANCELLED', billingMode: 'PREPAID', paymentAmount: 300000, cancelAmount: 300000, paymentMethod: '신용카드', receiptUrl: null },
    { paymentDate: '2026-03-17 10:00', memberName: '최영호', coachName: '조진성', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', billingMode: 'PREPAID', paymentAmount: 800000, cancelAmount: 0, paymentMethod: '신용카드', receiptUrl: null },
    { paymentDate: '2026-03-16 09:30', memberName: '정하늘', coachName: '최동석', membershipName: 'PT 3회', paymentStatus: 'FAILED',  billingMode: 'PER_SESSION', paymentAmount: 0, cancelAmount: 0, paymentMethod: '신용카드', receiptUrl: null },
    { paymentDate: '2026-03-15 13:15', memberName: '강미래', coachName: '이영진', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', billingMode: 'PREPAID', paymentAmount: 800000, cancelAmount: 0, paymentMethod: '체크카드', receiptUrl: null },
    { paymentDate: '2026-03-14 17:50', memberName: '윤서연', coachName: '조진성', membershipName: 'PT 10회', paymentStatus: 'COMPLETED', billingMode: 'PER_SESSION', paymentAmount: 500000, cancelAmount: 0, paymentMethod: '신용카드', receiptUrl: null },
    { paymentDate: '2026-03-13 12:00', memberName: '한도윤', coachName: '최동석', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', billingMode: 'PREPAID', paymentAmount: 800000, cancelAmount: 0, paymentMethod: '신용카드', receiptUrl: null },
];

const dummySettlementDaily: SettlementDailyItem[] = [
    { settlementDate: '2026-03-24', salesDate: '2026-03-20', totalCount: 2, salesAmount: 1600000, pgFee: 24000, pgVat: 2400, totalFee: 26400, settlementAmount: 1573600, settlementStatus: 'SETTLED' },
    { settlementDate: '2026-03-23', salesDate: '2026-03-19', totalCount: 2, salesAmount: 1300000, pgFee: 19500, pgVat: 1950, totalFee: 21450, settlementAmount: 1278550, settlementStatus: 'SETTLED' },
    { settlementDate: '2026-03-22', salesDate: '2026-03-18', totalCount: 2, salesAmount: 1100000, pgFee: 12000, pgVat: 1200, totalFee: 13200, settlementAmount: 1086800, settlementStatus: 'SETTLED' },
    { settlementDate: '2026-03-21', salesDate: '2026-03-17', totalCount: 1, salesAmount: 800000, pgFee: 12000, pgVat: 1200, totalFee: 13200, settlementAmount: 786800, settlementStatus: 'SETTLED' },
    { settlementDate: '-', salesDate: '2026-03-16', totalCount: 2, salesAmount: 1300000, pgFee: 19500, pgVat: 1950, totalFee: 21450, settlementAmount: 1278550, settlementStatus: 'PENDING' },
    { settlementDate: '-', salesDate: '2026-03-15', totalCount: 3, salesAmount: 2100000, pgFee: 31500, pgVat: 3150, totalFee: 34650, settlementAmount: 2065350, settlementStatus: 'PENDING' },
];

const dummySettlementPerCase: SettlementPerCaseItem[] = [
    { settlementDate: '2026-03-24', salesDate: '2026-03-20', memberName: '김민수', coachName: '이영진', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', paymentAmount: 800000, pgFee: 12000, vat: 1200, settlementAmount: 786800, paymentMethod: '신용카드/신한', settlementStatus: 'SETTLED' },
    { settlementDate: '2026-03-24', salesDate: '2026-03-20', memberName: '강미래', coachName: '이영진', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', paymentAmount: 800000, pgFee: 12000, vat: 1200, settlementAmount: 786800, paymentMethod: '체크카드/국민', settlementStatus: 'SETTLED' },
    { settlementDate: '2026-03-23', salesDate: '2026-03-19', memberName: '박지은', coachName: '최동석', membershipName: 'PT 10회', paymentStatus: 'COMPLETED', paymentAmount: 500000, pgFee: 7500, vat: 750, settlementAmount: 491750, paymentMethod: '체크카드/하나', settlementStatus: 'SETTLED' },
    { settlementDate: '2026-03-23', salesDate: '2026-03-19', memberName: '최영호', coachName: '조진성', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', paymentAmount: 800000, pgFee: 12000, vat: 1200, settlementAmount: 786800, paymentMethod: '신용카드/삼성', settlementStatus: 'SETTLED' },
    { settlementDate: '-', salesDate: '2026-03-18', memberName: '이수진', coachName: '이영진', membershipName: 'PT 5회', paymentStatus: 'CANCELLED', paymentAmount: 300000, pgFee: 0, vat: 0, settlementAmount: 0, paymentMethod: '신용카드/신한', settlementStatus: 'PENDING' },
    { settlementDate: '-', salesDate: '2026-03-17', memberName: '윤서연', coachName: '조진성', membershipName: 'PT 10회', paymentStatus: 'COMPLETED', paymentAmount: 500000, pgFee: 7500, vat: 750, settlementAmount: 491750, paymentMethod: '신용카드/현대', settlementStatus: 'PENDING' },
    { settlementDate: '-', salesDate: '2026-03-16', memberName: '한도윤', coachName: '최동석', membershipName: 'PT 20회', paymentStatus: 'COMPLETED', paymentAmount: 800000, pgFee: 12000, vat: 1200, settlementAmount: 786800, paymentMethod: '신용카드/국민', settlementStatus: 'PENDING' },
];

const dummyPaymentRequest: PaymentRequestItem[] = [
    { requestDate: '2026-03-24 09:00', expireDate: '2026-03-31 09:00', memberName: '김민수', coachName: '이영진', membershipName: 'PT 20회', itemName: 'PT 20회 수강권', requestAmount: 800000, requestStatus: 'WAITING', paymentDate: null },
    { requestDate: '2026-03-23 14:00', expireDate: '2026-03-30 14:00', memberName: '박지은', coachName: '최동석', membershipName: 'PT 10회', itemName: 'PT 10회 수강권', requestAmount: 500000, requestStatus: 'COMPLETED', paymentDate: '2026-03-23 15:30' },
    { requestDate: '2026-03-22 10:00', expireDate: '2026-03-29 10:00', memberName: '이수진', coachName: '이영진', membershipName: 'PT 5회', itemName: 'PT 5회 수강권', requestAmount: 300000, requestStatus: 'WAITING', paymentDate: null },
    { requestDate: '2026-03-15 11:00', expireDate: '2026-03-22 11:00', memberName: '최영호', coachName: '조진성', membershipName: 'PT 20회', itemName: 'PT 20회 수강권', requestAmount: 800000, requestStatus: 'EXPIRED', paymentDate: null },
    { requestDate: '2026-03-14 16:00', expireDate: '2026-03-21 16:00', memberName: '정하늘', coachName: '최동석', membershipName: 'PT 3회', itemName: 'PT 3회 수강권', requestAmount: 200000, requestStatus: 'CANCELLED', paymentDate: null },
    { requestDate: '2026-03-20 09:30', expireDate: '2026-03-27 09:30', memberName: '강미래', coachName: '이영진', membershipName: 'PT 20회', itemName: 'PT 20회 수강권', requestAmount: 800000, requestStatus: 'COMPLETED', paymentDate: '2026-03-20 18:00' },
    { requestDate: '2026-03-21 13:00', expireDate: '2026-03-28 13:00', memberName: '윤서연', coachName: '조진성', membershipName: 'PT 10회', itemName: 'PT 10회 수강권', requestAmount: 500000, requestStatus: 'WAITING', paymentDate: null },
];

// ──────────────────────────────────────
// Column Definitions
// ──────────────────────────────────────
const paymentColumnHelper = createColumnHelper<PaymentHistoryItem>();
const paymentColumns = [
    paymentColumnHelper.accessor('paymentDate', {
        id: 'paymentDate',
        header: '결제일시',
        cell: (info) => info.getValue(),
        size: 160,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('memberName', {
        id: 'memberName',
        header: '회원명',
        cell: (info) => <span className="block w-[150px] truncate" title={info.getValue()}>{info.getValue()}</span>,
        size: 170,
        minSize: 170,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('coachName', {
        id: 'coachName',
        header: '강사명',
        cell: (info) => info.getValue(),
        size: 90,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('membershipName', {
        id: 'membershipName',
        header: '수강권명',
        cell: (info) => info.getValue(),
        size: 160,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('paymentStatus', {
        id: 'paymentStatus',
        header: '결제상태',
        cell: (info) => {
            const status = info.getValue();
            const text = status === 'COMPLETED' ? '완료' : status === 'CANCELLED' ? '취소' : '결제실패';
            const color = status === 'COMPLETED' ? '#3D4244' : status === 'CANCELLED' ? '#FF7220' : '#FF2020';
            return (
                <RegularText fontSize={16} lineHeight={24} color={color}>
                    {text}
                </RegularText>
            );
        },
        size: 110,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('billingMode', {
        id: 'billingMode',
        header: '결제방식',
        cell: (info) => (info.getValue() === 'PREPAID' ? '선결제' : '수업당 결제'),
        size: 120,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('paymentAmount', {
        id: 'paymentAmount',
        header: '결제금액',
        cell: (info) => (info.getValue() === 0 ? '-' : convertToKRW(info.getValue())),
        size: 140,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('cancelAmount', {
        id: 'cancelAmount',
        header: '취소액',
        cell: (info) => (info.getValue() === 0 ? '-' : convertToKRW(info.getValue())),
        size: 140,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('paymentMethod', {
        id: 'paymentMethod',
        header: '결제수단',
        cell: (info) => info.getValue(),
        size: 120,
        enableSorting: false,
    }),
    paymentColumnHelper.accessor('receiptUrl', {
        id: 'receiptUrl',
        header: '영수증',
        cell: (info) => {
            const url = info.getValue();
            if (!url) return <RegularText fontSize={16} lineHeight={24} color="#6B7280">-</RegularText>;
            return (
                <RegularText
                    fontSize={16}
                    lineHeight={24}
                    color="#3D73DD"
                    className="cursor-pointer hover:underline"
                    onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                >
                    보기
                </RegularText>
            );
        },
        size: 80,
        enableSorting: false,
    }),
];

const dailyColumnHelper = createColumnHelper<SettlementDailyItem>();
const dailyColumns = [
    dailyColumnHelper.accessor('settlementDate', { id: 'settlementDate', header: '정산 입금일', cell: (info) => info.getValue(), size: 130, enableSorting: false }),
    dailyColumnHelper.accessor('salesDate', { id: 'salesDate', header: '매출일', cell: (info) => info.getValue(), size: 130, enableSorting: false }),
    dailyColumnHelper.accessor('totalCount', { id: 'totalCount', header: '결제+취소 건수', cell: (info) => `${info.getValue()}건`, size: 130, enableSorting: false }),
    dailyColumnHelper.accessor('salesAmount', { id: 'salesAmount', header: '매출액 (A)', cell: (info) => convertToKRW(info.getValue()), size: 150, enableSorting: false }),
    dailyColumnHelper.accessor('pgFee', { id: 'pgFee', header: 'PG수수료 (B)', cell: (info) => convertToKRW(info.getValue()), size: 140, enableSorting: false }),
    dailyColumnHelper.accessor('pgVat', { id: 'pgVat', header: '공제수수료 (C)', cell: (info) => convertToKRW(info.getValue()), size: 130, enableSorting: false }),
    dailyColumnHelper.accessor('totalFee', { id: 'totalFee', header: '수수료 합 (B+C)', cell: (info) => convertToKRW(info.getValue()), size: 140, enableSorting: false }),
    dailyColumnHelper.accessor('settlementAmount', { id: 'settlementAmount', header: '정산액 (A-B-C)', cell: (info) => convertToKRW(info.getValue()), size: 150, enableSorting: false }),
    dailyColumnHelper.accessor('settlementStatus', {
        id: 'settlementStatus',
        header: '정산상태',
        cell: (info) => {
            const settled = info.getValue() === 'SETTLED';
            return (
                <RegularText fontSize={16} lineHeight={24} color={settled ? '#3D73DD' : '#FF7220'}>
                    {settled ? '입금완료' : '정산예정'}
                </RegularText>
            );
        },
        size: 110,
        enableSorting: false,
    }),
];

const perCaseColumnHelper = createColumnHelper<SettlementPerCaseItem>();
const perCaseColumns = [
    perCaseColumnHelper.accessor('settlementDate', { id: 'settlementDate', header: '정산 입금일', cell: (info) => info.getValue(), size: 120, enableSorting: false }),
    perCaseColumnHelper.accessor('salesDate', { id: 'salesDate', header: '매출일', cell: (info) => info.getValue(), size: 110, enableSorting: false }),
    perCaseColumnHelper.accessor('memberName', { id: 'memberName', header: '회원명', cell: (info) => <span className="block w-[150px] truncate" title={info.getValue()}>{info.getValue()}</span>, size: 170, minSize: 170, enableSorting: false }),
    perCaseColumnHelper.accessor('coachName', { id: 'coachName', header: '강사명', cell: (info) => info.getValue(), size: 100, enableSorting: false }),
    perCaseColumnHelper.accessor('membershipName', { id: 'membershipName', header: '수강권명', cell: (info) => info.getValue(), size: 130, enableSorting: false }),
    perCaseColumnHelper.accessor('paymentStatus', {
        id: 'paymentStatus',
        header: '결제상태',
        cell: (info) => {
            const status = info.getValue();
            const text = status === 'COMPLETED' ? '완료' : status === 'CANCELLED' ? '취소' : '결제실패';
            const color = status === 'COMPLETED' ? '#3D4244' : status === 'CANCELLED' ? '#FF7220' : '#FF2020';
            return <RegularText fontSize={16} lineHeight={24} color={color}>{text}</RegularText>;
        },
        size: 90,
        enableSorting: false,
    }),
    perCaseColumnHelper.accessor('paymentAmount', { id: 'paymentAmount', header: '결제/취소액 (A)', cell: (info) => convertToKRW(info.getValue()), size: 140, enableSorting: false }),
    perCaseColumnHelper.accessor('pgFee', { id: 'pgFee', header: 'PG수수료 (B)', cell: (info) => convertToKRW(info.getValue()), size: 120, enableSorting: false }),
    perCaseColumnHelper.accessor('vat', { id: 'vat', header: '부가세 (C)', cell: (info) => convertToKRW(info.getValue()), size: 110, enableSorting: false }),
    perCaseColumnHelper.accessor('settlementAmount', { id: 'settlementAmount', header: '정산액 (A-B-C)', cell: (info) => convertToKRW(info.getValue()), size: 140, enableSorting: false }),
    perCaseColumnHelper.accessor('paymentMethod', { id: 'paymentMethod', header: '결제수단', cell: (info) => info.getValue(), size: 120, enableSorting: false }),
    perCaseColumnHelper.accessor('settlementStatus', {
        id: 'settlementStatus',
        header: '정산상태',
        cell: (info) => {
            const settled = info.getValue() === 'SETTLED';
            return <RegularText fontSize={16} lineHeight={24} color={settled ? '#3D73DD' : '#FF7220'}>{settled ? '입금완료' : '정산예정'}</RegularText>;
        },
        size: 100,
        enableSorting: false,
    }),
];

const requestColumnHelper = createColumnHelper<PaymentRequestItem>();
const requestColumns = [
    requestColumnHelper.accessor('requestDate', { id: 'requestDate', header: '요청일시', cell: (info) => info.getValue(), size: 150, enableSorting: false }),
    requestColumnHelper.accessor('expireDate', {
        id: 'expireDate',
        header: '만료일시',
        cell: (info) => {
            const expireDate = info.getValue();
            const status = info.row.original.requestStatus;
            if (status !== 'WAITING') return expireDate;
            const diffHours = dayjs(expireDate).diff(dayjs(), 'hour');
            const isExpiringSoon = diffHours >= 0 && diffHours <= 48;
            return (
                <Row className="items-center">
                    <RegularText fontSize={16} lineHeight={24} color={isExpiringSoon ? '#FF7220' : '#3D4244'}>
                        {expireDate}
                    </RegularText>
                    {isExpiringSoon && (
                        <span className="ml-[6px] px-[6px] py-[1px] bg-[#FFF3EC] rounded-[4px]">
                            <DemiLightText fontSize={13} lineHeight={18} color="#FF7220">
                                {diffHours <= 24 ? '임박' : 'D-2'}
                            </DemiLightText>
                        </span>
                    )}
                </Row>
            );
        },
        size: 180,
        enableSorting: false,
    }),
    requestColumnHelper.accessor('memberName', { id: 'memberName', header: '회원명', cell: (info) => <span className="block w-[150px] truncate" title={info.getValue()}>{info.getValue()}</span>, size: 170, minSize: 170, enableSorting: false }),
    requestColumnHelper.accessor('coachName', { id: 'coachName', header: '강사명', cell: (info) => info.getValue(), size: 100, enableSorting: false }),
    requestColumnHelper.accessor('membershipName', { id: 'membershipName', header: '수강권명', cell: (info) => info.getValue(), size: 140, enableSorting: false }),
    requestColumnHelper.accessor('itemName', { id: 'itemName', header: '결제 항목명', cell: (info) => info.getValue(), size: 140, enableSorting: false }),
    requestColumnHelper.accessor('requestAmount', { id: 'requestAmount', header: '요청 금액', cell: (info) => convertToKRW(info.getValue()), size: 130, enableSorting: false }),
    requestColumnHelper.accessor('requestStatus', {
        id: 'requestStatus',
        header: '요청 상태',
        cell: (info) => {
            const status = info.getValue();
            const map: Record<RequestStatus, { text: string; color: string }> = {
                WAITING: { text: '대기중', color: '#FF7220' },
                COMPLETED: { text: '결제완료', color: '#3D73DD' },
                EXPIRED: { text: '만료', color: '#6B7280' },
                CANCELLED: { text: '취소', color: '#FF2020' },
            };
            const { text, color } = map[status];
            return <RegularText fontSize={16} lineHeight={24} color={color}>{text}</RegularText>;
        },
        size: 100,
        enableSorting: false,
    }),
    requestColumnHelper.accessor('paymentDate', {
        id: 'paymentDateCol',
        header: '결제일시',
        cell: (info) => info.getValue() ?? '-',
        size: 150,
        enableSorting: false,
    }),
    requestColumnHelper.display({
        id: 'reRequest',
        header: '재요청',
        cell: (info) => {
            const status = info.row.original.requestStatus;
            if (status !== 'EXPIRED') return <RegularText fontSize={16} lineHeight={24} color="#6B7280">-</RegularText>;
            return (
                <button
                    className="px-[12px] py-[4px] bg-[#3D73DD] rounded-[6px] hover:bg-[#2D5FBF] transition-colors"
                    onClick={() => {
                        // TODO: API 연동 후 재요청 로직 구현
                        console.log('재요청:', info.row.original.memberName, info.row.original.membershipName);
                    }}
                >
                    <DemiLightText fontSize={13} lineHeight={20} color="white">재요청</DemiLightText>
                </button>
            );
        },
        size: 90,
        enableSorting: false,
    }),
];

// ──────────────────────────────────────
// Sub Components
// ──────────────────────────────────────

/** 요약 카드 셀 */
const SummaryCard = ({ label, value, subText, highlight }: { label: string; value: string; subText?: string; highlight?: boolean }) => (
    <td className="py-[22px] border border-solid border-[#D9D9D9]">
        <Col>
            <RegularText fontSize={23} lineHeight={24} color={highlight ? '#3D73DD' : '#3D4244'} className="mb-[9px]">
                {value}
            </RegularText>
            {subText && (
                <DemiLightText fontSize={13} lineHeight={20} color="#6B7280">
                    {subText}
                </DemiLightText>
            )}
        </Col>
    </td>
);

/** 요약 테이블 */
const SummaryTable = ({ items }: { items: { label: string; value: string; subText?: string; highlight?: boolean }[] }) => (
    <div className="w-full">
        <table className="min-w-full bg-white border border-solid border-gray-300 text-center mb-[40px]">
            <thead>
                <tr className="bg-gray-100 border-solid border-b h-[40px] justify-center items-center">
                    {items.map((item) => (
                        <th key={item.label} className="h-full justify-center items-center content-center border border-solid border-[#D9D9D9] bg-[#F0F2F3]">
                            <RegularText fontSize={16} lineHeight={24} color="#6B7280" letterSpacing={-0.32}>
                                {item.label}
                            </RegularText>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                <tr>
                    {items.map((item) => (
                        <SummaryCard key={item.label} {...item} />
                    ))}
                </tr>
            </tbody>
        </table>
    </div>
);

/** 퀵 날짜 버튼 */
const QuickDateButton = ({ label, isSelected, onClick }: { label: string; isSelected: boolean; onClick: () => void }) => (
    <button
        className="flex items-center justify-center rounded-[7px] px-[14px] py-[7px] mr-[8px]"
        style={{ backgroundColor: isSelected ? '#C9DBFF66' : '#F2F4F6' }}
        onClick={onClick}
    >
        <DemiLightText fontSize={15} lineHeight={22} color={isSelected ? colors.primary : '#3D4244'}>
            {label}
        </DemiLightText>
    </button>
);

/** 커스텀 기간 선택 버튼 */
const CustomDateRangeButton = ({
    isSelected,
    startDate,
    endDate,
    onClick,
}: {
    isSelected: boolean;
    startDate: Date | null;
    endDate: Date | null;
    onClick: () => void;
}) => (
    <button
        className="flex items-center justify-center rounded-[7px] px-[14px] py-[7px] mr-[8px] relative"
        style={{ backgroundColor: isSelected ? '#C9DBFF66' : '#F2F4F6' }}
        onClick={onClick}
    >
        <SvgIcon name={'SvgCalendar'} size={16} fill={colors.trans} stroke={isSelected ? colors.primary : '#70747C'} strokeWidth={1.2} className="mr-[6px]" />
        <DemiLightText fontSize={15} lineHeight={22} color={isSelected ? colors.primary : '#3D4244'}>
            {isSelected && startDate && endDate
                ? `${dayjs(startDate).format('YY.MM.DD')} ~ ${dayjs(endDate).format('YY.MM.DD')}`
                : '기간 설정'}
        </DemiLightText>
    </button>
);

/** 페이지네이션 컴포넌트 (SalesListPage 통일) */
const paginationComponent = ({
    firstPage,
    nextPage,
    canNextPage,
    previousPage,
    canPreviousPage,
}: {
    firstPage: () => void;
    previousPage: () => void;
    canPreviousPage: boolean;
    nextPage: () => void;
    canNextPage: boolean;
    pageIndex?: number;
    totalPageIndex?: number;
    setPageIndex?: (pageIndex: number) => void;
}) => (
    <Row className="absolute items-center flex right-0 bottom-0 mb-[50px]">
        <RegularText
            fontSize={16}
            lineHeight={23}
            color="#3D4244"
            className={`mr-[16px] ${canPreviousPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => !canPreviousPage && firstPage()}
        >
            처음으로
        </RegularText>
        <button
            className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={previousPage}
            disabled={canPreviousPage}
        >
            <SvgIcon name={'SvgLeftStrokeArrow'} size={15} fill={colors.trans} stroke={'#70747C'} strokeWidth={1.2} className="mr-[7px]" />
            <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                이전
            </RegularText>
        </button>
        <button
            className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={nextPage}
            disabled={canNextPage}
        >
            <RegularText fontSize={16} lineHeight={23} color="#3D4244" className="mr-[7px]">
                다음
            </RegularText>
            <SvgIcon name={'SvgRightStrokeArrow'} size={15} fill={colors.trans} stroke={'#70747C'} strokeWidth={1.2} />
        </button>
    </Row>
);

/** 회원 선택 모달 (결제 요청용) */
const ModalSelectMember = ({
    onClose,
    onSelect,
}: {
    onClose: () => void;
    onSelect: (member: { memberId: string; memberName: string; coachId: string; coachName: string }) => void;
}) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const [centerUserList, setCenterUserList] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await getAllCoachMemberListAPI(authObject?.adminId, '', '', authorization);
                if (res?.status === 200) {
                    setCenterUserList(res.data.centerUserList ?? []);
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchUsers();
    }, []);

    // 전체 회원 리스트 평탄화 (회원명은 alias 필드 사용)
    const allMembers = centerUserList.flatMap((coach: any) =>
        (coach.members ?? []).map((member: any) => ({
            memberId: member.memberId,
            memberName: member.alias ?? member.memberName ?? member.name ?? '',
            coachId: coach.coachId,
            coachName: coach.coachName,
        })),
    );

    const filteredMembers = searchText
        ? allMembers.filter((m: any) => m.memberName.includes(searchText) || m.coachName.includes(searchText))
        : allMembers;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
            <div
                className="bg-white rounded-[20px] w-[480px] max-h-[600px] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* 헤더 */}
                <div className="px-[28px] pt-[28px] pb-[16px]">
                    <MediumText fontSize={22} lineHeight={28} color="#3D4244" className="mb-[20px]">
                        결제 요청할 회원 선택
                    </MediumText>
                    <input
                        type="text"
                        placeholder="회원명 또는 강사명 검색"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="w-full rounded-[10px] bg-[#F2F4F6] px-[16px] py-[12px] text-[15px] text-[#3D4244] outline-none placeholder-[#9CA3AF]"
                        autoFocus
                    />
                </div>
                {/* 리스트 */}
                <div className="flex-1 overflow-y-auto px-[28px] pb-[28px]">
                    {isLoading ? (
                        <Col className="items-center py-[40px]">
                            <RegularText fontSize={16} lineHeight={24} color="#6B7280">
                                불러오는 중...
                            </RegularText>
                        </Col>
                    ) : filteredMembers.length === 0 ? (
                        <Col className="items-center py-[40px]">
                            <RegularText fontSize={16} lineHeight={24} color="#6B7280">
                                검색 결과가 없어요
                            </RegularText>
                        </Col>
                    ) : (
                        filteredMembers.map((member: any) => (
                            <div
                                key={`${member.coachId}-${member.memberId}`}
                                className="flex items-center justify-between py-[14px] px-[12px] rounded-[10px] cursor-pointer hover:bg-[#F2F4F6] transition-colors"
                                onClick={() => onSelect(member)}
                            >
                                <Col>
                                    <RegularText fontSize={16} lineHeight={24} color="#3D4244">
                                        {member.memberName}
                                    </RegularText>
                                    <DemiLightText fontSize={13} lineHeight={20} color="#6B7280">
                                        담당: {member.coachName}
                                    </DemiLightText>
                                </Col>
                                <SvgIcon name={'SvgRightStrokeArrow'} size={14} fill={colors.trans} stroke={'#9CA3AF'} strokeWidth={1.5} />
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

/** 필터 드롭다운 (간이 버전) */
const SimpleFilter = ({ value, options, onChange }: { value: string; options: { code: string; value: string }[]; onChange: (code: string) => void }) => (
    <select
        className="flex items-center rounded-[7px] px-[12px] py-[8px] mr-[12px] bg-[#F2F4F6] text-[15px] text-[#3D4244] border-none outline-none cursor-pointer"
        value={value}
        onChange={(e) => onChange(e.target.value)}
    >
        {options.map((opt) => (
            <option key={opt.code} value={opt.code}>
                {opt.value}
            </option>
        ))}
    </select>
);

/** 취소 제외 토글 */
const ExcludeCancelToggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center cursor-pointer mr-[16px]">
        <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="w-[18px] h-[18px] mr-[6px] accent-[#3D73DD] cursor-pointer"
        />
        <DemiLightText fontSize={15} lineHeight={22} color="#3D4244">
            취소 제외
        </DemiLightText>
    </label>
);

// ──────────────────────────────────────
// Main Component
// ──────────────────────────────────────
const RappoPaySettlementPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<SubTab>('paymentHistory');
    const [settlementSubTab, setSettlementSubTab] = useState<SettlementSubTab>('daily');
    const [quickDate, setQuickDate] = useState<QuickDate | 'custom'>('month');
    const [excludeCancel, setExcludeCancel] = useState(false);

    // 커스텀 기간 선택
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [startDate, setStartDate] = useState<Date | null>(dayjs().startOf('month').toDate());
    const [endDate, setEndDate] = useState<Date | null>(dayjs().endOf('month').toDate());

    // 결제 요청 모달 상태
    const [isMemberSelectModalOpen, setIsMemberSelectModalOpen] = useState(false);

    // Filters
    const [selectedCoach, setSelectedCoach] = useState('all');
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
    const [selectedRequestStatus, setSelectedRequestStatus] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [lookupBasis, setLookupBasis] = useState<'salesDate' | 'settlementDate'>('salesDate');

    const coachOptions = [
        { code: 'all', value: '강사 전체' },
        { code: 'coach1', value: '이영진' },
        { code: 'coach2', value: '최동석' },
        { code: 'coach3', value: '조진성' },
    ];
    const paymentStatusOptions = [
        { code: 'all', value: '결제상태 전체' },
        { code: 'COMPLETED', value: '완료' },
        { code: 'CANCELLED', value: '취소' },
        { code: 'FAILED', value: '결제실패' },
    ];
    const requestStatusOptions = [
        { code: 'all', value: '요청상태 전체' },
        { code: 'WAITING', value: '대기중' },
        { code: 'COMPLETED', value: '결제완료' },
        { code: 'EXPIRED', value: '만료' },
        { code: 'CANCELLED', value: '취소' },
    ];
    const lookupBasisOptions = [
        { code: 'salesDate', value: '매출일 기준' },
        { code: 'settlementDate', value: '정산 입금일 기준' },
    ];

    const handleQuickDate = (type: QuickDate) => {
        setQuickDate(type);
        if (type === 'custom') {
            setIsCalendarOpen(true);
            return;
        }
        setIsCalendarOpen(false);
        switch (type) {
            case 'today':
                setStartDate(dayjs().startOf('day').toDate());
                setEndDate(dayjs().endOf('day').toDate());
                break;
            case 'week':
                setStartDate(dayjs().subtract(1, 'week').startOf('day').toDate());
                setEndDate(dayjs().endOf('day').toDate());
                break;
            case 'month':
                setStartDate(dayjs().startOf('month').toDate());
                setEndDate(dayjs().endOf('month').toDate());
                break;
            case 'lastMonth':
                setStartDate(dayjs().subtract(1, 'month').startOf('month').toDate());
                setEndDate(dayjs().subtract(1, 'month').endOf('month').toDate());
                break;
        }
    };

    // Filtered data
    const filteredPaymentHistory = dummyPaymentHistory.filter((item) => {
        if (excludeCancel && item.paymentStatus === 'CANCELLED') return false;
        if (selectedPaymentStatus !== 'all' && item.paymentStatus !== selectedPaymentStatus) return false;
        if (selectedCoach !== 'all') {
            const coachName = coachOptions.find((c) => c.code === selectedCoach)?.value;
            if (item.coachName !== coachName) return false;
        }
        if (searchText && !item.memberName.includes(searchText)) return false;
        return true;
    });

    const filteredSettlementDaily = dummySettlementDaily;
    const filteredSettlementPerCase = dummySettlementPerCase.filter((item) => {
        if (selectedCoach !== 'all') {
            const coachName = coachOptions.find((c) => c.code === selectedCoach)?.value;
            if (item.coachName !== coachName) return false;
        }
        if (searchText && !item.memberName.includes(searchText)) return false;
        return true;
    });

    const filteredPaymentRequest = dummyPaymentRequest.filter((item) => {
        if (selectedRequestStatus !== 'all' && item.requestStatus !== selectedRequestStatus) return false;
        if (selectedCoach !== 'all') {
            const coachName = coachOptions.find((c) => c.code === selectedCoach)?.value;
            if (item.coachName !== coachName) return false;
        }
        if (searchText && !item.memberName.includes(searchText)) return false;
        return true;
    });

    // Summary calculations — 결제 내역
    const paymentCompletedItems = filteredPaymentHistory.filter((i) => i.paymentStatus === 'COMPLETED');
    const paymentCancelledItems = filteredPaymentHistory.filter((i) => i.paymentStatus === 'CANCELLED');
    const paymentCompletedMembers = new Set(paymentCompletedItems.map((i) => i.memberName)).size;
    const paymentCancelledMembers = new Set(paymentCancelledItems.map((i) => i.memberName)).size;

    const paymentSummary = {
        totalPayment: paymentCompletedItems.reduce((sum, i) => sum + i.paymentAmount, 0),
        totalPaymentCount: paymentCompletedItems.length,
        totalPaymentMembers: paymentCompletedMembers,
        totalCancel: paymentCancelledItems.reduce((sum, i) => sum + i.cancelAmount, 0),
        totalCancelCount: paymentCancelledItems.length,
        totalCancelMembers: paymentCancelledMembers,
    };
    const netPayment = paymentSummary.totalPayment - paymentSummary.totalCancel;
    const netCount = paymentSummary.totalPaymentCount + paymentSummary.totalCancelCount;
    const netMembers = new Set(filteredPaymentHistory.filter((i) => i.paymentStatus !== 'FAILED').map((i) => i.memberName)).size;

    // Summary calculations — 정산 내역
    const settlementSummary = {
        totalSales: dummySettlementDaily.reduce((sum, i) => sum + i.salesAmount, 0),
        totalSalesCount: dummySettlementDaily.reduce((sum, i) => sum + i.totalCount, 0),
        totalFee: dummySettlementDaily.reduce((sum, i) => sum + i.totalFee, 0),
        totalFeeCount: dummySettlementDaily.length,
        settledAmount: dummySettlementDaily.filter((i) => i.settlementStatus === 'SETTLED').reduce((sum, i) => sum + i.settlementAmount, 0),
        settledCount: dummySettlementDaily.filter((i) => i.settlementStatus === 'SETTLED').reduce((sum, i) => sum + i.totalCount, 0),
        pendingAmount: dummySettlementDaily.filter((i) => i.settlementStatus === 'PENDING').reduce((sum, i) => sum + i.settlementAmount, 0),
        pendingCount: dummySettlementDaily.filter((i) => i.settlementStatus === 'PENDING').reduce((sum, i) => sum + i.totalCount, 0),
    };

    // Summary calculations — 결제 요청
    const requestCompletedItems = filteredPaymentRequest.filter((i) => i.requestStatus === 'COMPLETED');
    const requestWaitingItems = filteredPaymentRequest.filter((i) => i.requestStatus === 'WAITING');
    const requestExpiredItems = filteredPaymentRequest.filter((i) => i.requestStatus === 'EXPIRED');

    const requestSummary = {
        total: filteredPaymentRequest.length,
        totalAmount: filteredPaymentRequest.reduce((sum, i) => sum + i.requestAmount, 0),
        completed: requestCompletedItems.length,
        completedAmount: requestCompletedItems.reduce((sum, i) => sum + i.requestAmount, 0),
        waiting: requestWaitingItems.length,
        waitingAmount: requestWaitingItems.reduce((sum, i) => sum + i.requestAmount, 0),
        expired: requestExpiredItems.length,
        expiredAmount: requestExpiredItems.reduce((sum, i) => sum + i.requestAmount, 0),
    };

    // Reset filters on tab change
    useEffect(() => {
        setSelectedCoach('all');
        setSelectedPaymentStatus('all');
        setSelectedRequestStatus('all');
        setSearchText('');
        setExcludeCancel(false);
    }, [activeTab]);

    const subTabs: { key: SubTab; label: string }[] = [
        { key: 'paymentHistory', label: '결제 내역' },
        { key: 'settlementHistory', label: '정산 내역' },
        { key: 'paymentRequest', label: '결제 요청' },
    ];

    return (
        <div className="flex flex-col w-full h-full pt-[29px] px-[46px] bg-white rounded-[27px] mb-[50px]">
            {/* Sub Tabs */}
            <Row className="items-center mb-[32px] relative">
                {subTabs.map((tab) => (
                    <div
                        key={tab.key}
                        className="cursor-pointer mr-[36px] pb-[10px]"
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <MediumText
                            fontSize={22}
                            lineHeight={28}
                            color={activeTab === tab.key ? '#3D4244' : '#6B7280'}
                        >
                            {tab.label}
                        </MediumText>
                        {activeTab === tab.key && <div className="w-full h-[4px] bg-[#3D4244] mt-[8px] rounded-[2px]" />}
                    </div>
                ))}
            </Row>

            {/* ─── 결제 내역 탭 ─── */}
            {activeTab === 'paymentHistory' && (
                <>
                    {/* 요약 카드 */}
                    <SummaryTable
                        items={[
                            { label: '총 결제금액', value: convertToKRW(paymentSummary.totalPayment), subText: `${paymentSummary.totalPaymentCount}건/${paymentSummary.totalPaymentMembers}명` },
                            { label: '취소금액', value: convertToKRW(paymentSummary.totalCancel), subText: `${paymentSummary.totalCancelCount}건/${paymentSummary.totalCancelMembers}명` },
                            {
                                label: '순결제금액',
                                value: convertToKRW(netPayment),
                                subText: `${netCount}건/${netMembers}명`,
                                highlight: true,
                            },
                        ]}
                    />

                    {/* 필터 영역 */}
                    <Row className="flex w-full justify-between mb-[24px] items-center flex-wrap gap-y-[12px]">
                        <Row className="items-center relative">
                            <QuickDateButton label="오늘" isSelected={quickDate === 'today'} onClick={() => handleQuickDate('today')} />
                            <QuickDateButton label="1주일" isSelected={quickDate === 'week'} onClick={() => handleQuickDate('week')} />
                            <QuickDateButton label="당월" isSelected={quickDate === 'month'} onClick={() => handleQuickDate('month')} />
                            <QuickDateButton label="전월" isSelected={quickDate === 'lastMonth'} onClick={() => handleQuickDate('lastMonth')} />
                            <CustomDateRangeButton
                                isSelected={quickDate === 'custom'}
                                startDate={startDate}
                                endDate={endDate}
                                onClick={() => { setQuickDate('custom'); setIsCalendarOpen((prev) => !prev); }}
                            />
                            {isCalendarOpen && (
                                <RangeCalendar
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    setIsCalendar={setIsCalendarOpen}
                                    isPrevMonth
                                    isNextMonth
                                />
                            )}
                        </Row>
                        <Row className="items-center">
                            <SimpleFilter value={selectedCoach} options={coachOptions} onChange={setSelectedCoach} />
                            <SimpleFilter value={selectedPaymentStatus} options={paymentStatusOptions} onChange={setSelectedPaymentStatus} />
                            <ExcludeCancelToggle checked={excludeCancel} onChange={setExcludeCancel} />
                            <Row className="items-center bg-[#F2F4F6] rounded-[7px] px-[12px] py-[5px] mr-[12px] cursor-pointer">
                                <SvgIcon name={'SvgExcel'} size={20} fill={colors.trans} className="mr-[7px]" />
                                <DemiLightText fontSize={16} lineHeight={23} color="#3D4244" letterSpacing={-0.32}>
                                    엑셀
                                </DemiLightText>
                            </Row>
                            <input
                                type="text"
                                placeholder="회원명 검색"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="rounded-[7px] bg-[#F2F4F6] px-[14px] py-[8px] text-[15px] text-[#3D4244] outline-none w-[154px] placeholder-[#9CA3AF]"
                            />
                        </Row>
                    </Row>

                    {/* 건수 표시 */}
                    <Row className="mb-[16px]">
                        <RegularText fontSize={18} lineHeight={24} color="#3D4244" letterSpacing={-0.32}>
                            {`총 ${filteredPaymentHistory.length}건`}
                        </RegularText>
                    </Row>

                    {/* 테이블 */}
                    {filteredPaymentHistory.length > 0 ? (
                        <Table showHorizontalScrollHint data={filteredPaymentHistory} columns={paymentColumns} width={1584} className="mb-[100px]" isPagination customPaginationComponent={paginationComponent} />
                    ) : (
                        <EmptyState />
                    )}
                </>
            )}

            {/* ─── 정산 내역 탭 ─── */}
            {activeTab === 'settlementHistory' && (
                <>
                    {/* 요약 카드 */}
                    <SummaryTable
                        items={[
                            { label: '총 결제금액', value: convertToKRW(settlementSummary.totalSales), subText: `${settlementSummary.totalSalesCount}건` },
                            { label: '총 수수료 (PG 수수료 및 부가세 포함)', value: convertToKRW(settlementSummary.totalFee), subText: `${settlementSummary.totalFeeCount}건` },
                            { label: '입금 정산액', value: convertToKRW(settlementSummary.settledAmount), subText: `${settlementSummary.settledCount}건`, highlight: true },
                            { label: '정산 예정 금액', value: convertToKRW(settlementSummary.pendingAmount), subText: `${settlementSummary.pendingCount}건` },
                        ]}
                    />

                    {/* 일자별 / 건별 서브탭 */}
                    <Row className="items-center mb-[24px]">
                        <div className="relative flex items-center">
                            {(['daily', 'perCase'] as SettlementSubTab[]).map((tab) => (
                                <div
                                    key={tab}
                                    className="cursor-pointer mr-[24px] pb-[8px]"
                                    onClick={() => setSettlementSubTab(tab)}
                                >
                                    <RegularText
                                        fontSize={18}
                                        lineHeight={24}
                                        color={settlementSubTab === tab ? '#3D4244' : '#6B7280'}
                                    >
                                        {tab === 'daily' ? '일자별' : '건별'}
                                    </RegularText>
                                    {settlementSubTab === tab && <div className="w-full h-[3px] bg-[#3D4244] mt-[6px] rounded-[2px]" />}
                                </div>
                            ))}
                        </div>
                    </Row>

                    {/* 필터 영역 */}
                    <Row className="flex w-full justify-between mb-[24px] items-center flex-wrap gap-y-[12px]">
                        <Row className="items-center relative">
                            <QuickDateButton label="오늘" isSelected={quickDate === 'today'} onClick={() => handleQuickDate('today')} />
                            <QuickDateButton label="1주일" isSelected={quickDate === 'week'} onClick={() => handleQuickDate('week')} />
                            <QuickDateButton label="당월" isSelected={quickDate === 'month'} onClick={() => handleQuickDate('month')} />
                            <QuickDateButton label="전월" isSelected={quickDate === 'lastMonth'} onClick={() => handleQuickDate('lastMonth')} />
                            <CustomDateRangeButton
                                isSelected={quickDate === 'custom'}
                                startDate={startDate}
                                endDate={endDate}
                                onClick={() => { setQuickDate('custom'); setIsCalendarOpen((prev) => !prev); }}
                            />
                            {isCalendarOpen && (
                                <RangeCalendar
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    setIsCalendar={setIsCalendarOpen}
                                    isPrevMonth
                                    isNextMonth
                                />
                            )}
                            <div className="mx-[8px] h-[24px] w-[1px] bg-[#D9D9D9]" />
                            <SimpleFilter value={lookupBasis} options={lookupBasisOptions} onChange={(v) => setLookupBasis(v as 'salesDate' | 'settlementDate')} />
                        </Row>
                        <Row className="items-center">
                            <SimpleFilter value={selectedCoach} options={coachOptions} onChange={setSelectedCoach} />
                            <Row className="items-center bg-[#F2F4F6] rounded-[7px] px-[12px] py-[5px] mr-[12px] cursor-pointer">
                                <SvgIcon name={'SvgExcel'} size={20} fill={colors.trans} className="mr-[7px]" />
                                <DemiLightText fontSize={16} lineHeight={23} color="#3D4244" letterSpacing={-0.32}>
                                    엑셀
                                </DemiLightText>
                            </Row>
                            {settlementSubTab === 'perCase' && (
                                <input
                                    type="text"
                                    placeholder="회원명 검색"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="rounded-[7px] bg-[#F2F4F6] px-[14px] py-[8px] text-[15px] text-[#3D4244] outline-none w-[154px] placeholder-[#9CA3AF]"
                                />
                            )}
                        </Row>
                    </Row>

                    {/* 정산 계좌 */}
                    <Row className="mb-[16px] items-center">
                        <DemiLightText fontSize={15} lineHeight={22} color="#6B7280" className="mr-[8px]">
                            정산계좌
                        </DemiLightText>
                        <RegularText fontSize={15} lineHeight={22} color="#3D4244">
                            하나은행 ***-******-0504
                        </RegularText>
                    </Row>

                    {/* 테이블 */}
                    {settlementSubTab === 'daily' ? (
                        filteredSettlementDaily.length > 0 ? (
                            <Table showHorizontalScrollHint data={filteredSettlementDaily} columns={dailyColumns} width={1584} className="mb-[100px]" isPagination customPaginationComponent={paginationComponent} />
                        ) : (
                            <EmptyState />
                        )
                    ) : filteredSettlementPerCase.length > 0 ? (
                        <Table showHorizontalScrollHint data={filteredSettlementPerCase} columns={perCaseColumns} width={1584} className="mb-[100px]" isPagination customPaginationComponent={paginationComponent} />
                    ) : (
                        <EmptyState />
                    )}
                </>
            )}

            {/* ─── 결제 요청 탭 ─── */}
            {activeTab === 'paymentRequest' && (
                <>
                    {/* 요약 카드 */}
                    <SummaryTable
                        items={[
                            { label: '총 요청 건수', value: `${requestSummary.total}건`, subText: convertToKRW(requestSummary.totalAmount) },
                            { label: '결제 완료', value: `${requestSummary.completed}건`, subText: convertToKRW(requestSummary.completedAmount), highlight: true },
                            { label: '미결제 (대기중)', value: `${requestSummary.waiting}건`, subText: convertToKRW(requestSummary.waitingAmount) },
                            { label: '만료', value: `${requestSummary.expired}건`, subText: convertToKRW(requestSummary.expiredAmount) },
                        ]}
                    />

                    {/* 필터 영역 */}
                    <Row className="flex w-full justify-between mb-[24px] items-center flex-wrap gap-y-[12px]">
                        <Row className="items-center relative">
                            <QuickDateButton label="오늘" isSelected={quickDate === 'today'} onClick={() => handleQuickDate('today')} />
                            <QuickDateButton label="1주일" isSelected={quickDate === 'week'} onClick={() => handleQuickDate('week')} />
                            <QuickDateButton label="당월" isSelected={quickDate === 'month'} onClick={() => handleQuickDate('month')} />
                            <QuickDateButton label="전월" isSelected={quickDate === 'lastMonth'} onClick={() => handleQuickDate('lastMonth')} />
                            <CustomDateRangeButton
                                isSelected={quickDate === 'custom'}
                                startDate={startDate}
                                endDate={endDate}
                                onClick={() => { setQuickDate('custom'); setIsCalendarOpen((prev) => !prev); }}
                            />
                            {isCalendarOpen && (
                                <RangeCalendar
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    setIsCalendar={setIsCalendarOpen}
                                    isPrevMonth
                                    isNextMonth
                                />
                            )}
                        </Row>
                        <Row className="items-center">
                            <SimpleFilter value={selectedCoach} options={coachOptions} onChange={setSelectedCoach} />
                            <SimpleFilter value={selectedRequestStatus} options={requestStatusOptions} onChange={setSelectedRequestStatus} />
                            <Row className="items-center bg-[#F2F4F6] rounded-[7px] px-[12px] py-[5px] mr-[12px] cursor-pointer">
                                <SvgIcon name={'SvgExcel'} size={20} fill={colors.trans} className="mr-[7px]" />
                                <DemiLightText fontSize={16} lineHeight={23} color="#3D4244" letterSpacing={-0.32}>
                                    엑셀
                                </DemiLightText>
                            </Row>
                            <input
                                type="text"
                                placeholder="회원명 검색"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="rounded-[7px] bg-[#F2F4F6] px-[14px] py-[8px] text-[15px] text-[#3D4244] outline-none w-[154px] placeholder-[#9CA3AF]"
                            />
                        </Row>
                    </Row>

                    {/* 건수 표시 */}
                    <Row className="mb-[16px]">
                        <RegularText fontSize={18} lineHeight={24} color="#3D4244" letterSpacing={-0.32}>
                            {`총 ${filteredPaymentRequest.length}건`}
                        </RegularText>
                    </Row>

                    {/* 테이블 */}
                    {filteredPaymentRequest.length > 0 ? (
                        <Table showHorizontalScrollHint data={filteredPaymentRequest} columns={requestColumns} width={1584} className="mb-[100px]" isPagination customPaginationComponent={paginationComponent} />
                    ) : (
                        <EmptyState />
                    )}

                    {/* 플로팅 결제 요청 버튼 */}
                    <div
                        className="fixed bottom-[60px] right-[60px] z-[100] flex items-center justify-center bg-[#3D73DD] rounded-[16px] px-[24px] py-[16px] cursor-pointer shadow-lg hover:bg-[#2D5FBF] transition-colors"
                        onClick={() => setIsMemberSelectModalOpen(true)}
                    >
                        <SvgIcon name={'SvgPlus'} size={20} fill={'white'} className="mr-[8px]" />
                        <MediumText fontSize={17} lineHeight={24} color="white">
                            결제 요청하기
                        </MediumText>
                    </div>
                </>
            )}

            {/* 회원 선택 모달 */}
            {isMemberSelectModalOpen && (
                <ModalSelectMember
                    onClose={() => setIsMemberSelectModalOpen(false)}
                    onSelect={(member) => {
                        setIsMemberSelectModalOpen(false);
                        startTransition(() => { overlay.open(({ unmount }) => (
                            <ModalMembershipCreate
                                setIsVisible={(v) => { if (!v) unmount(); }}
                                onClickCreateNewMembership={() => {
                                    unmount();
                                    navigate('/classPass');
                                }}
                                memberName={member.memberName}
                                coachName={member.coachName}
                                coachId={member.coachId}
                                memberId={member.memberId}
                                type="memberProfile"
                                createAlert={(title: string, message: string, isError?: boolean, buttonContent?: string, callBack?: any) => {
                                    overlay.open(({ unmount: unmountAlert }) => (
                                        <ModalAlertOneButton
                                            title={title}
                                            content={message}
                                            isError={isError}
                                            buttonContent={buttonContent}
                                            callBack={() => {
                                                callBack && callBack();
                                            }}
                                            isTwoButton={false}
                                            twoButtonContent={null}
                                            twoButtonCallBack={null}
                                            alertStateReset={unmountAlert}
                                        />
                                    ));
                                }}
                                defaultPaymentMethod="rappoPay"
                            />
                        )); });
                    }}
                />
            )}
        </div>
    );
};

const EmptyState = () => (
    <Col className="flex flex-col overflow-x-auto w-full">
        <Col className="w-full items-center h-[500px]">
            <MediumText fontSize={20} lineHeight={29} letterSpacing={-0.3} color="#3D4244" className="mb-[8px] mt-[100px]">
                조회 결과가 없어요
            </MediumText>
            <RegularText fontSize={16} lineHeight={24} color="#6B7280">
                입력한 정보가 올바른지 확인해주세요.
            </RegularText>
        </Col>
    </Col>
);

export default RappoPaySettlementPage;
